import React, { useState, useEffect } from 'react';
import { colors } from '../styles/styles';
import useWindowResize from '../hooks/useWindowResize';
import { getDeepAnalysis } from '../services/openAiService';

const ShuffleView = ({ thoughts }) => {
    const [shuffledThoughts, setShuffledThoughts] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [deepAnalysis, setDeepAnalysis] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const isMobile = useWindowResize();
    const [visible, setVisible] = useState(false);

    // Shuffle thoughts when component mounts or thoughts change
    useEffect(() => {
        if (!thoughts.length) return;
        const shuffled = [...thoughts].sort(() => Math.random() - 0.5);
        setShuffledThoughts(shuffled);
        setCurrentIndex(0);
        setIsExpanded(false);
    }, [thoughts]);

    // Reset expanded state when changing thoughts
    useEffect(() => {
        setIsExpanded(false);
    }, [currentIndex]);

    const handleNext = async () => {
        setIsLoading(true);
        setIsExpanded(false);
        // Random delay between 300ms and 800ms
        const delay = Math.floor(Math.random() * 500) + 300;
        await new Promise(resolve => setTimeout(resolve, delay));
        setCurrentIndex((prev) => (prev + 1) % shuffledThoughts.length);
        setIsLoading(false);
    };

    const handlePrevious = () => {
        setIsExpanded(false);
        setCurrentIndex((prev) => 
            prev === 0 ? shuffledThoughts.length - 1 : prev - 1
        );
    };

    const handleExpand = () => {
        setIsExpanded(true);
    };

    const handleGoDeeper = async () => {
        if (!currentThought || isAnalyzing) return;
        
        setIsAnalyzing(true);
        try {
            const analysis = await getDeepAnalysis(currentThought.thought);
            setDeepAnalysis(analysis);
            setIsExpanded(true);
        } catch (error) {
            console.error('Error getting deep analysis:', error);
        }
        setIsAnalyzing(false);
    };

    // Reset analysis when changing thoughts
    useEffect(() => {
        setDeepAnalysis(null);
    }, [currentIndex]);

    const formatDateTime = (timestamp) => {
        const date = new Date(timestamp);
        const options = { 
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        };
        const timeOptions = {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        };
        return `${date.toLocaleDateString('en-US', options)} at ${date.toLocaleTimeString('en-US', timeOptions)}`;
    };

    useEffect(() => {
        const timer = setTimeout(() => setVisible(true), 10);
        return () => clearTimeout(timer);
    }, []);

    if (!shuffledThoughts.length) {
        return (
            <div style={styles.container}>
            </div>
        );
    }

    const currentThought = shuffledThoughts[currentIndex];

    const NavigationButtons = () => (
        <>
            <button 
                onClick={handlePrevious}
                style={styles.navButton}
                className="ios-button"
                disabled={isLoading}
            >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            <button 
                onClick={handleNext}
                style={styles.navButton}
                className="ios-button"
                disabled={isLoading}
            >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        </>
    );

    const ThoughtContent = () => {
        const displayText = !isExpanded 
            ? currentThought.thought.substring(0, 100)
            : currentThought.thought;

        return (
            <div style={styles.thoughtContent}>
                <div style={styles.thoughtText}>
                    <p style={{
                        margin: '0',
                        wordBreak: 'break-word',
                    }}>
                        {displayText}
                        {!isExpanded && currentThought.thought.length > 100 && (
                            <>
                                ... <button
                                    onClick={handleExpand}
                                    className="expand-pulse"
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        padding: '0',
                                        color: colors.accent.white,
                                        opacity: 0.8,
                                        cursor: 'pointer',
                                        fontSize: 'inherit',
                                        display: 'inline',
                                        marginLeft: '4px',
                                    }}
                                >
                                    Show more
                                </button>
                            </>
                        )}
                    </p>
                </div>

                <div style={{
                    ...styles.metadata,
                    marginTop: '20px',
                }}>
                    <span style={styles.date}>
                        {formatDateTime(currentThought.timestamp)}
                    </span>
                </div>

                {!deepAnalysis && (
                    <div style={styles.deeperButtonContainer}>
                        <div style={styles.subtleDivider} />
                        <button
                            onClick={handleGoDeeper}
                            disabled={isAnalyzing}
                            style={styles.deeperButton}
                            className="deeper-button"
                        >
                            {isAnalyzing ? 'Analyzing...' : 'Go Deeper'}
                        </button>
                    </div>
                )}

                {deepAnalysis && (
                    <div style={styles.analysisContainer}>
                        <div style={styles.divider} />
                        <div style={styles.analysisContent}>
                            {deepAnalysis.split('\n').map((line, index) => (
                                <p key={index} style={{
                                    margin: line.startsWith('#') ? '16px 0 8px' : '8px 0',
                                    fontSize: line.startsWith('#') ? '18px' : '16px',
                                    fontWeight: line.startsWith('#') ? '600' : '400',
                                }}>
                                    {line.replace(/^#+ /, '')}
                                </p>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            style={{
                ...styles.container,
                animation: 'fadeIn 0.3s cubic-bezier(0.2, 0, 0, 1)',
                opacity: visible ? 1 : 0,
            }}
        >
            {isMobile ? (
                <>
                    <div style={{
                        ...styles.thoughtContainer,
                        opacity: isLoading ? 0.5 : 1,
                        transform: isLoading ? 'scale(0.98)' : 'scale(1)',
                    }}>
                        <ThoughtContent />
                    </div>
                    <div style={styles.mobileNavigation}>
                        <NavigationButtons />
                    </div>
                </>
            ) : (
                <>
                    <div style={{
                        ...styles.thoughtContainer,
                        opacity: isLoading ? 0.5 : 1,
                        transform: isLoading ? 'scale(0.98)' : 'scale(1)',
                    }}>
                        <ThoughtContent />
                    </div>
                    <div style={styles.mobileNavigation}>
                        <NavigationButtons />
                    </div>
                </>
            )}

            <style>
                {`
                    @keyframes ios-tap-pulse {
                        0% { opacity: 1; transform: scale(1); }
                        35% { opacity: 0.4; transform: scale(0.97); }
                        100% { opacity: 1; transform: scale(1); }
                    }
                    
                    .ios-button:active {
                        animation: ios-tap-pulse 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
                    }
                    
                    .ios-tapped {
                        animation: ios-tap-pulse 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
                    }

                    @keyframes expand-pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(.95); }
                        100% { transform: scale(1); }
                    }

                    .expand-pulse:active {
                        animation: expand-pulse 300ms;
                    }

                    @keyframes loading-pulse {
                        0% { transform: scale(0.5); opacity: 0.2; }
                        50% { transform: scale(1.2); opacity: 1; }
                        100% { transform: scale(0.5); opacity: 0.2; }
                    }

                    .reveal-button {
                        transition: transform 0.3s ease;
                    }

                    .reveal-button:hover svg {
                        transform: translateY(3px);
                    }

                    .reveal-button svg {
                        transition: transform 0.2s ease;
                    }

                    @keyframes loading-progress {
                        0% { transform: scaleX(0); opacity: 0.8; }
                        100% { transform: scaleX(1); opacity: 0.2; }
                    }

                    .loadingIndicator {
                        width: 20px;
                        height: 2px;
                        background-color: ${colors.accent.white}22;
                        border-radius: 1px;
                        overflow: hidden;
                    }

                    .loadingBar {
                        width: 100%;
                        height: 100%;
                        background-color: ${colors.accent.white};
                        animation: loading-progress 1s ease-in-out infinite;
                        transform-origin: left;
                    }

                    .deeper-button {
                        transition: all 0.2s ease;
                    }

                    .deeper-button:hover {
                        transform: scale(1.05);
                        background-color: ${colors.accent.white};
                        color: ${colors.background};
                    }

                    .deeper-button:active {
                        transform: scale(0.95);
                    }

                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                `}
            </style>
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        backgroundColor: colors.background,
        position: 'relative',
        paddingTop: '120px',
    },
    thoughtContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        maxWidth: '800px',
        width: '100%',
    },
    thoughtContent: {
        flex: 1,
        textAlign: 'left',
        backgroundColor: colors.cardBackground,
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '600px',
        width: '100%',
        margin: '0 auto',
        position: 'relative',
    },
    thoughtText: {
        fontSize: '16px',
        lineHeight: '1.6',
        color: colors.textPrimary,
    },
    metadata: {
        display: 'flex',
        justifyContent: 'flex-start',
        marginTop: '20px',
    },
    date: {
        color: colors.textTertiary,
        fontSize: '14px',
    },
    navButton: {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '16px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        WebkitTapHighlightColor: 'transparent',
    },
    expandButton: {
        background: 'transparent',
        border: '1px solid ' + colors.accent.white,
        borderRadius: '6px',
        padding: '8px 16px',
        color: colors.accent.white,
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginTop: '10px',
    },
    emptyText: {
        color: colors.textSecondary,
        fontSize: '16px',
    },
    mobileNavigation: {
        position: 'fixed',
        bottom: '40px',
        left: '0',
        right: '0',
        display: 'flex',
        justifyContent: 'center',
        gap: '40px',
        padding: '20px',
        backgroundColor: 'transparent',
        zIndex: 10,
    },
    loadingDot: {
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        backgroundColor: colors.accent.white,
        animation: 'loading-pulse 1s infinite',
    },
    loadingIndicator: {
        width: '20px',
        height: '2px',
        backgroundColor: `${colors.accent.white}22`,
        borderRadius: '1px',
        overflow: 'hidden',
    },
    loadingBar: {
        width: '100%',
        height: '100%',
        backgroundColor: colors.accent.white,
        animation: 'loading-progress 1s ease-in-out infinite',
        transformOrigin: 'left',
    },
    deeperButton: {
        background: 'transparent',
        border: `1px solid ${colors.accent.white}33`,
        borderRadius: '16px',
        padding: '6px 12px',
        color: colors.accent.white,
        fontSize: '13px',
        cursor: 'pointer',
        fontWeight: '500',
        transition: 'all 0.2s ease',
        opacity: 0.7,
    },
    deeperButtonContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '24px',
        gap: '12px',
    },
    subtleDivider: {
        height: '1px',
        backgroundColor: `${colors.accent.white}11`,
        margin: '0',
        width: '40px',
    },
    divider: {
        height: '1px',
        backgroundColor: `${colors.accent.white}22`,
        margin: '20px 0',
        width: '100%',
    },
    analysisContainer: {
        marginTop: '20px',
    },
    analysisContent: {
        color: colors.textSecondary,
        fontSize: '16px',
        lineHeight: '1.6',
    },
};

export default ShuffleView; 